"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import IndexBadges from "@/components/IndexBadges";

type RsiRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  date: string;
  rsi_prev: number;
  rsi_curr: number;
  indices?: string[];
  newly_below?: boolean;
};

const formatNumber = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });

// RSI Concept Visualization Component
function RsiVisualization() {
  return (
    <div style={{
      background: "rgba(15, 23, 42, 0.6)",
      border: "1px solid rgba(148, 163, 184, 0.2)",
      borderRadius: "12px",
      padding: "1.5rem",
      marginBottom: "2rem",
    }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem", color: "var(--blue-text)" }}>
        Understanding RSI
      </h3>
      
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.5rem" }}>
        {/* Left side - Explanation */}
        <div>
          <p style={{ color: "var(--color-text-muted)", lineHeight: 1.6, marginBottom: "1rem", fontSize: "0.9rem" }}>
            <strong style={{ color: "var(--color-text)" }}>RSI</strong> (Relative Strength Index) measures momentum on a scale of 0-100, showing overbought/oversold conditions.
          </p>
          
          <div style={{ background: "rgba(59, 130, 246, 0.1)", padding: "0.75rem", borderRadius: "8px", marginBottom: "1rem", fontSize: "0.85rem" }}>
            <div style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "var(--blue-text)" }}>RSI 0-30</strong>
              <div style={{ color: "var(--color-text-muted)" }}>Oversold — weakness can persist</div>
            </div>
            <div style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "#fbbf24" }}>RSI 30-70</strong>
              <div style={{ color: "var(--color-text-muted)" }}>Normal trading range</div>
            </div>
            <div>
              <strong style={{ color: "var(--color-danger)" }}>RSI 70-100</strong>
              <div style={{ color: "var(--color-text-muted)" }}>Overbought - watch for pullback</div>
            </div>
          </div>

          <div style={{ padding: "0.75rem", background: "rgba(34, 197, 94, 0.1)", borderLeft: "3px solid var(--color-success)", borderRadius: "4px", fontSize: "0.85rem" }}>
            <strong style={{ color: "var(--color-success)" }}>✓ Bullish Crossover</strong>
            <div style={{ color: "var(--color-text-muted)", marginTop: "0.3rem" }}>RSI crosses ABOVE your selected threshold level</div>
          </div>
        </div>

        {/* Right side - Visual Scale */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <svg width="100%" height="220" viewBox="0 0 300 220" style={{ background: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
            {/* Scale background */}
            <rect x="40" y="20" width="30" height="180" fill="rgba(0,0,0,0.3)" rx="4" />
            
            {/* Color zones */}
            <rect x="40" y="20" width="30" height="45" fill="rgba(16, 185, 129, 0.3)" opacity="0.7" />
            <rect x="40" y="65" width="30" height="90" fill="rgba(251, 191, 36, 0.2)" opacity="0.7" />
            <rect x="40" y="155" width="30" height="45" fill="rgba(248, 113, 113, 0.3)" opacity="0.7" />
            
            {/* Zone labels */}
            <text x="85" y="50" fontSize="11" fill="var(--color-danger)" fontWeight="bold">Overbought (70-100)</text>
            <text x="85" y="110" fontSize="11" fill="#fbbf24" fontWeight="bold">Normal (30-70)</text>
            <text x="85" y="175" fontSize="11" fill="var(--color-success)" fontWeight="bold">Oversold (0-30)</text>
            
            {/* RSI scale markers */}
            <text x="25" y="35" fontSize="10" fill="var(--color-text-muted)" textAnchor="end">100</text>
            <text x="25" y="110" fontSize="10" fill="var(--color-text-muted)" textAnchor="end">50</text>
            <text x="25" y="205" fontSize="10" fill="var(--color-text-muted)" textAnchor="end">0</text>
            
            {/* Signal indicator */}
            <circle cx="55" cy="130" r="5" fill="var(--color-success)" />
            <line x1="70" y1="130" x2="220" y2="130" stroke="var(--color-success)" strokeWidth="2" strokeDasharray="5,5" />
            <text x="230" y="135" fontSize="11" fill="var(--color-success)" fontWeight="bold">Signal Level</text>
          </svg>
          
          <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.5rem" }}>
            Watch stocks below 30 or scan upward crossings of your chosen threshold.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RsiScannerPage() {
  const [rows, setRows] = useState<RsiRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rsiLevel, setRsiLevel] = useState<number>(60);
  const [view, setView] = useState<"cross" | "below">("cross");
  const [sessionDate, setSessionDate] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      setRows([]);
      setSessionDate(null);
      try {
        const isDefault = view === "below" || rsiLevel === 60;
        const params = new URLSearchParams();
        params.set("level", rsiLevel.toString());
        params.set("limit", "20");
        const url = isDefault
          ? "/api/outputs/scanner_rsi.json"
          : `/api/scanner/rsi-cross?${params.toString()}`;
        const res = await fetch(url, { cache: "no-store", signal: controller.signal });
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.error ?? json.details ?? `HTTP ${res.status}`);
        }
        if (controller.signal.aborted) return;
        const report = view === "below" ? json.below_30 : json;
        if (!report || report.ok === false || !Array.isArray(report.rows)) {
          throw new Error("This RSI report is unavailable. Refresh the scanner snapshot and try again.");
        }
        setRows(report.rows);
        setSessionDate(report.session_date ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(err instanceof Error ? err.message : "Failed to load RSI scan.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [rsiLevel, view, retry]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">RSI Scanner</h1>
          <p className="page-subtitle">Track RSI below 30 and upward momentum crossings</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {/* RSI Concept Visualization */}
          <RsiVisualization />

          <div role="group" aria-label="RSI scan view" style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
            {([ ["cross", "Upward crossings"], ["below", "RSI below 30"] ] as const).map(([id, label]) => (
              <button key={id} type="button" aria-pressed={view === id} onClick={() => setView(id)}
                style={{ minHeight: 44, padding: "0.75rem 1rem", borderRadius: 8, border: "1px solid var(--color-border)", background: view === id ? "var(--color-primary)" : "var(--color-surface)", color: "var(--color-text)", cursor: "pointer" }}>{label}</button>
            ))}
          </div>
          {view === "below" && <p style={{ color: "var(--color-text-muted)", marginBottom: "1rem", lineHeight: 1.6 }}>
            RSI(14) strictly below 30, lowest first. Includes stocks already below 30, not just new crossings.
            Active stocks with trading volume on the latest available price session and at least 30 price records.
            Oversold describes momentum; it does not confirm a reversal or a buy signal.
          </p>}
          {/* Controls */}
          {view === "cross" && <div style={{
            background: "linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(16, 185, 129, 0.05))",
            border: "1px solid rgba(34, 197, 94, 0.2)",
            borderRadius: "12px",
            padding: "1.5rem",
            marginBottom: "1.5rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap" }}>
              <div>
                <label style={{ display: "block", fontWeight: 600, color: "var(--color-text)", fontSize: "0.95rem", marginBottom: "0.5rem" }}>
                  Signal Strength Level
                </label>
                <select
                  value={rsiLevel}
                  onChange={(e) => setRsiLevel(Number(e.target.value))}
                  style={{
                    padding: "0.75rem 1rem",
                    borderRadius: "8px",
                    border: "1px solid var(--color-border)",
                    background: "rgba(255,255,255,0.05)",
                    color: "var(--color-text)",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "0.95rem",
                    minWidth: "220px",
                  }}
                >
                  <option value={50}>🟢 RSI 50 - Early Signal</option>
                  <option value={60}>🟡 RSI 60 - Strong Signal</option>
                  <option value={70}>🔴 RSI 70 - Overbought</option>
                </select>
              </div>
              <div style={{
                padding: "0.75rem 1.25rem",
                background: "rgba(255,255,255,0.05)",
                borderRadius: "8px",
                border: "1px solid rgba(255,255,255,0.1)",
              }}>
                <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem", marginBottom: "0.15rem" }}>Definition</div>
                <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                  {rsiLevel === 50 && "Early entry • More opportunities • Higher risk"}
                  {rsiLevel === 60 && "Balanced • Confirmed strength • Moderate risk"}
                  {rsiLevel === 70 && "Late entry • Strong momentum • Watch for reversal"}
                </div>
              </div>
            </div>
          </div>}
          <p role="status" style={{ color: "var(--color-text-muted)", marginBottom: "1rem" }}>
            {loading ? "Loading…" : error ? "Report unavailable" : `${rows.length} stocks${sessionDate ? ` · Session ${sessionDate}` : ""}`}
          </p>
          <div className="results-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(138, 180, 248, 0.18)", borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Symbol</th>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Company</th>
                  <th style={{ textAlign: "left", padding: "0.85rem", color: "var(--color-text)" }}>Sector</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>Close</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>RSI (Prev → Now)</th>
                  <th style={{ textAlign: "right", padding: "0.85rem", color: "var(--color-text)" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                      Loading RSI scan...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={6} style={{ padding: "1rem", color: "#fca5a5" }}>
                      {error} <button type="button" onClick={() => setRetry((n) => n + 1)}>Try again</button>
                    </td>
                  </tr>
                )}
                {!loading && !error && rows.map((r, idx) => {
                  const rsiGain = r.rsi_curr - r.rsi_prev;
                  return (
                    <tr key={`${r.symbol}-${idx}`} style={{ borderBottom: "1px solid var(--color-border)", transition: "background 0.2s" }}>
                      <td style={{ padding: "0.85rem", fontWeight: 700, fontSize: "1rem" }}>
                        <Link href={`/stock/${encodeURIComponent(r.symbol)}`} style={{ color: "var(--blue-text)" }}>{r.symbol}</Link>
                        {view === "below" && r.newly_below && <small style={{ display: "block", color: "var(--color-text-muted)" }}>New below 30</small>}
                        <IndexBadges indices={r.indices ?? []} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                      </td>
                      <td style={{ padding: "0.85rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>{r.name}</td>
                      <td style={{ padding: "0.85rem", fontSize: "0.85rem" }}>
                        <span style={{
                          padding: "0.35rem 0.65rem",
                          background: "rgba(255,255,255,0.05)",
                          borderRadius: "6px",
                          color: "var(--color-text-muted)",
                        }}>{r.sector}</span>
                      </td>
                      <td style={{ padding: "0.85rem", textAlign: "right", fontWeight: 600 }}>{formatNumber(r.close)}</td>
                      <td style={{ padding: "0.85rem", textAlign: "right" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.5rem" }}>
                          <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>{formatNumber(r.rsi_prev, 1)}</span>
                          <span style={{ color: "var(--color-success)" }}>→</span>
                          <span style={{ color: view === "below" ? "var(--color-text)" : "var(--color-success)", fontWeight: 700, fontSize: "1.05rem" }}>{formatNumber(r.rsi_curr, 1)}</span>
                          <span style={{
                            padding: "0.25rem 0.5rem",
                            background: "rgba(34, 197, 94, 0.15)",
                            color: rsiGain < 0 ? "var(--color-danger)" : "var(--color-success)",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}>{rsiGain > 0 ? "+" : ""}{formatNumber(rsiGain, 1)}</span>
                        </div>
                      </td>
                      <td style={{ padding: "0.85rem", textAlign: "right", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{r.date}</td>
                    </tr>
                  );
                })}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: "1rem", color: "var(--color-text-muted)" }}>
                      {view === "below" ? "No eligible stocks have RSI below 30 for this session." : "No RSI crosses found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
